import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { ActionMenu } from '@lumapps/lumx-menu/components/ActionMenu';
import { mdiCheckAll, mdiCog, mdiDelete, mdiDeleteForever } from '@lumapps/lumx/icons';
import { ColorPalette } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { Link } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { getUserSettingsRoute, USER_SETTINGS_VIEWS } from '@lumapps/user-settings/routes';

import { DATA_NAMESPACE } from '../../constants';
import { isNotificationsBetaEnabled } from '../../ducks/selectors';
import { NOTIFICATIONS_CENTER } from '../../keys';

export interface NotificationListHeaderContextMenuProps {
    /** callback to delete all notifications */
    onDeleteAll: () => void;
    /** callback to mark all notifications as read (optional, for legacy mode) */
    onSetAllAsRead: () => void;
    /** callback to redirect to notification settings page */
    onSettingsClick: (evt: React.MouseEvent) => void;
    /** show "Mark all as read" option (default: false for new UI, true for legacy) */
    showMarkAllAsRead?: boolean;
    /** show "Delete all" option (default: true) */
    showDeleteAll?: boolean;
}

/**
 * Context menu for notifications
 */
export const NotificationListHeaderContextMenu: React.FC<NotificationListHeaderContextMenuProps> = ({
    onDeleteAll,
    onSetAllAsRead,
    onSettingsClick,
    showMarkAllAsRead,
    showDeleteAll,
}) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(DATA_NAMESPACE);
    const isNotificationsEnabled = useSelector(isNotificationsBetaEnabled);

    return (
        <ActionMenu label={translateKey(GLOBAL.OPTIONS)} {...get({ element: 'button', action: 'more' })}>
            {isNotificationsEnabled ? (
                <>
                    {showMarkAllAsRead && (
                        <ActionMenu.Item
                            key={NOTIFICATIONS_CENTER.MARK_ALL_AS_READ}
                            onClick={onSetAllAsRead}
                            icon={mdiCheckAll}
                            {...get({ element: 'mark-all-as-read' })}
                        >
                            {translateKey(NOTIFICATIONS_CENTER.MARK_ALL_AS_READ)}
                        </ActionMenu.Item>
                    )}
                    <ActionMenu.Item
                        key={NOTIFICATIONS_CENTER.NOTIFICATIONS_SETTINGS}
                        onClick={onSettingsClick}
                        icon={mdiCog}
                        {...get({ element: 'settings' })}
                        as={Link}
                        to={getUserSettingsRoute(USER_SETTINGS_VIEWS.NOTIFICATIONS)}
                    >
                        {translateKey(NOTIFICATIONS_CENTER.NOTIFICATIONS_SETTINGS)}
                    </ActionMenu.Item>
                    {(showDeleteAll ?? true) && (
                        <>
                            <ActionMenu.Divider />
                            <ActionMenu.Item
                                key={GLOBAL.DELETE_ALL}
                                onClick={onDeleteAll}
                                icon={mdiDelete}
                                color={ColorPalette.red}
                                {...get({ element: 'delete-all' })}
                            >
                                {translateKey(GLOBAL.DELETE_ALL)}
                            </ActionMenu.Item>
                        </>
                    )}
                </>
            ) : (
                <>
                    <ActionMenu.Item
                        key={NOTIFICATIONS_CENTER.MARK_ALL_AS_READ}
                        onClick={onSetAllAsRead}
                        icon={mdiCheckAll}
                        {...get({ element: 'mark-all-as-read' })}
                    >
                        {translateKey(NOTIFICATIONS_CENTER.MARK_ALL_AS_READ)}
                    </ActionMenu.Item>

                    <ActionMenu.Item
                        key={GLOBAL.DELETE_ALL}
                        onClick={onDeleteAll}
                        icon={mdiDeleteForever}
                        color={ColorPalette.red}
                        {...get({ element: 'delete-all' })}
                    >
                        {translateKey(GLOBAL.DELETE_ALL)}
                    </ActionMenu.Item>
                    <ActionMenu.Divider />
                    <ActionMenu.Item
                        key={NOTIFICATIONS_CENTER.NOTIFICATIONS_SETTINGS}
                        onClick={onSettingsClick}
                        icon={mdiCog}
                        {...get({ element: 'settings' })}
                        as={Link}
                        to={getUserSettingsRoute(USER_SETTINGS_VIEWS.NOTIFICATIONS)}
                    >
                        {translateKey(NOTIFICATIONS_CENTER.NOTIFICATIONS_SETTINGS)}
                    </ActionMenu.Item>
                </>
            )}
        </ActionMenu>
    );
};
